/**
 * PageManager
 * Manages full-page content switching between section pages and Chat
 *
 * State Machine:
 * - main--dashboard: Dashboard visible, input visible (centered on desktop, fixed on mobile)
 * - main--chat: Chat visible, input sticky at bottom
 * - main--fullpage: Discovery/Insight pages visible, input hidden
 */

import { Tooltip } from '../utils/Tooltip.js';
import { willBaseRouteShowChat } from '../router.js';

export class PageManager {
  /**
   * @param {Object} elements - DOM element references
   * @param {HTMLElement} elements.pageContainer - Page container element
   * @param {HTMLElement} elements.dashboard - Dashboard element
   * @param {EventBus} eventBus - Event bus for module communication
   */
  constructor(elements, eventBus) {
    this.pageContainer = elements.pageContainer;
    this.dashboard = elements.dashboard;
    this.chatContainer = document.getElementById('chat-container');
    this.headerContainer = document.querySelector('.header-container');
    this.eventBus = eventBus;

    // Main element for state machine
    this.main = document.querySelector('.main');
    this.chatInputArea = document.getElementById('chat-input-area');

    // State
    this.currentPage = null; // null = dashboard; otherwise a page ID such as 'assessments' or 'chat'
    this.previousPage = null;
    this.currentMainState = null; // 'dashboard', 'chat', 'fullpage'
    this.isTransitioning = false;

    // Navigation serialization. Every showPage/showDashboard call appends a
    // task to `_navQueue` and is assigned a monotonic `_navToken`. When the
    // task's turn arrives, it checks whether its token is still the latest;
    // intermediate calls that were superseded by a newer one resolve `false`
    // so route handlers skip section activation. This guarantees the visible
    // page eventually converges to the latest target even when calls race
    // an in-flight CSS transition (the original `isTransitioning` early-
    // return left URL and visible page out of sync).
    this._navToken = 0;
    this._navQueue = Promise.resolve();

    // Orientation header lock. Set by setOrientationInitialState() and cleared
    // by transitionOrientationToChat()/showDashboard(). While true, page-driven
    // header-visibility logic skips the unhide branch — orientation's pre-name
    // hide wins over showPage('chat')'s default "show header" rule regardless
    // of which side fires first.
    this._orientationInitialActive = false;

    // Transition durations (match CSS)
    this.fadeOutDuration = 200;      // Dashboard content fade out
    this.fadeInDuration = 200;       // Chat content + header fade in
    this.inputSlideDelay = 100;      // Pause before input slides
    this.inputSlideDuration = 400;   // Input slide duration

    // Get all page elements
    this.pages = {
      practices: document.getElementById('practices-page'),
      insights: document.getElementById('insights-page'),
      upgrade: document.getElementById('upgrade-page'),
      subscription: document.getElementById('subscription-page'),
      discovery: document.getElementById('discovery-page'),
      partner: document.getElementById('partner-page'),
      notes: document.getElementById('notes-page'),
      bridge: document.getElementById('bridge-page'),
      'account-settings': document.getElementById('account-settings-page'),
      help: document.getElementById('help-page'),
      memory: document.getElementById('memory-page'),
      search: document.getElementById('search-page'),
      chat: this.chatContainer
    };

    // Pages that hide the header (full-page views)
    this.pagesWithHiddenHeader = ['assessments', 'practices', 'insights', 'upgrade', 'subscription', 'discovery', 'partner', 'notes', 'bridge', 'account-settings', 'help', 'memory', 'search', 'dashboard'];

    // Pages that show the toolbar header
    this.pagesWithToolbar = ['assessments', 'practices', 'insights', 'upgrade', 'subscription', 'discovery', 'partner', 'notes', 'bridge', 'account-settings', 'help', 'memory', 'search'];

    // Pages that use the fullpage state (input hidden)
    this.fullpagePages = ['assessments', 'practices', 'insights', 'upgrade', 'subscription', 'discovery', 'partner', 'notes', 'bridge', 'account-settings', 'help', 'memory', 'search'];

    // Page toolbar header elements
    this.toolbar = document.getElementById('page-toolbar-header');
    this.toolbarActions = document.getElementById('page-toolbar-header-actions');
    this.toolbarRightActions = document.getElementById('page-toolbar-header-right-actions');
    this.closeButton = document.getElementById('page-header-close-btn');
    this.menuButton = document.getElementById('page-header-menu-btn');

    // Document title labels per page (null = dashboard).
    // Rendered as `<Label> | Encouple` to front-load the distinctive token.
    this.pageTitles = {
      chat: 'Chat',
      practices: 'Experiments',
      insights: 'Library',
      upgrade: 'Upgrade',
      subscription: 'Subscription',
      discovery: 'Discovery',
      partner: 'Partner',
      notes: 'Notes',
      bridge: 'Bridge',
      'account-settings': 'Account Settings',
      help: 'Help',
      memory: 'Memory',
      // Page header reads "History"; the sidebar entry stays "Search" (verb =
      // the affordance, noun = the destination).
      search: 'History'
    };

    // Track if initial page state has been set (to skip transition on first change)
    this.hasSetInitialPage = false;

    // Registry of pageId → section instance, populated by app.js after sections
    // are constructed (see setPageSections). Used by deactivateCurrent() to
    // tear down the outgoing section when showPage/showDashboard swaps pages.
    this.pageSections = {};

    this.initialize();
  }

  /**
   * Register the section instances keyed by pageId.
   *
   * Called from app.js after initializeSections() runs. PageManager itself is
   * constructed before sections exist, so injection cannot happen at construct
   * time. The registry need only include pages whose section owns DOM
   * observers, listeners, timers, or in-flight work that must be torn down on
   * navigation. Chat is intentionally not a section and has no entry.
   *
   * @param {Object<string, {deactivate: function}>} registry - { pageId: section }
   */
  setPageSections(registry) {
    this.pageSections = registry || {};
  }

  /**
   * Deactivate the section currently owning the visible page, if any.
   *
   * Looks up `this.pageSections[this.currentPage]` and calls its `deactivate()`
   * method, swallowing any throw with a warn log so one misbehaving section
   * cannot break navigation. Called from showPage/showDashboard before the
   * page swap and from app.cleanupBeforeLogout on sign-out.
   *
   * Section `deactivate()` implementations must be idempotent and should not
   * throw — throws are caught here only as a safety net.
   */
  deactivateCurrent() {
    const section = this.currentPage ? this.pageSections[this.currentPage] : null;
    if (!section || typeof section.deactivate !== 'function') return;
    try {
      section.deactivate();
    } catch (err) {
      console.warn(`[PageManager] deactivate() threw for page "${this.currentPage}":`, err);
    }
  }

  /**
   * Initialize page manager
   */
  initialize() {
    // Cache sidebar items that can have active state. "New chat" represents
    // the dashboard (null page); the rest map 1:1 to a page/section.
    //
    // Search is deliberately absent. It is not a destination in the nav list —
    // it is a utility control living in the sidebar header beside the collapse
    // toggle (see the header-control block in sidebar.css), and neither of that
    // pair takes a selected state. Adding it here would give the /search page a
    // highlighted button unlike anything else in the header.
    this.activatableSidebarItems = document.querySelectorAll(
      '.sidebar__item--new-chat, ' +
      '.sidebar__item[data-page="assessments"], ' +
      '.sidebar__item[data-page="practices"], ' +
      '.sidebar__item[data-page="insights"], ' +
      '.sidebar__item[data-page="discovery"], .sidebar__item[data-page="notes"], ' +
      '.sidebar__item[data-page="bridge"], ' +
      '.sidebar__item[data-section="help"], ' +
      '.sidebar__item[data-section="profile"]'
    );

    // Bind close button click handler
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.handleCloseButtonClick());
    }

    // Bind menu button click handler (toggles sidebar)
    if (this.menuButton) {
      this.menuButton.addEventListener('click', () => this.handleMenuButtonClick());
    }

    // Standardized tooltips for the toolbar's static-label buttons (e.g. Menu),
    // via the shared Tooltip utility — same approach/styling as the sidebar,
    // #mode-header (HeaderManager), and the right panel. Static markup, so one
    // pass here survives for the page's lifetime. The Close button is wired
    // separately below because its label is dynamic.
    if (this.toolbar) {
      Tooltip.setupAll(this.toolbar, '[data-tooltip]', { position: 'below' });
    }

    // Close doesn't dismiss an overlay in place — it navigates to `/`, which
    // the router resolves to either the active chat or the dashboard. Name the
    // real destination so the overlay-navigation flow is legible. getText is
    // evaluated on each hover, and willBaseRouteShowChat reuses the router's
    // own chat-vs-dashboard predicates so the label can't drift from reality.
    // below-end (under the X, right-aligned): a left-side tooltip would sit on
    // top of the section-mounted primary action in the right-actions slot.
    if (this.closeButton) {
      Tooltip.setup(this.closeButton, {
        position: 'below-end',
        getText: () => (willBaseRouteShowChat(window.app) ? 'Back to chat' : 'Back to dashboard')
      });
    }

    // The page-info `?` lives in each page's own centred title (see
    // renderPageInfoTitle in components/pageInfoPanel.js), which every section
    // re-renders freely. Delegating from the page container rather than binding
    // per section means one listener survives every re-render and keeps the
    // routing in one place — sections only have to emit the markup.
    if (this.pageContainer) {
      this.pageContainer.addEventListener('click', (e) => {
        if (e.target.closest('[data-action="page-info"]')) this.handlePageInfoClick();
      });
    }

    // Roll up sidebar badge state onto the toolbar menu button (static
    // markup, so a single bind here survives for the page's lifetime — same
    // pattern as HeaderManager's #header-menu-btn).
    if (this.eventBus) {
      this.eventBus.on('badge:state-changed', () => this.updateMenuButtonBadge());
    }
    this.updateMenuButtonBadge();

    // Hide the toolbar's close button while the right panel is open — the
    // panel has its own close button, so showing both is confusing.
    if (this.eventBus) {
      this.eventBus.on('right-panel:open', (payload) => this._handleRightPanelOpen(payload));
      this.eventBus.on('right-panel:opened', () => this._setCloseButtonHiddenForPanel(true));
      this.eventBus.on('right-panel:closed', () => this._setCloseButtonHiddenForPanel(false));
    }

    // Note: Don't call showDashboard() here - router determines initial page state
    // This prevents header flash when directly navigating to pages like /library
  }

  /**
   * Toggle the toolbar close button's (and right-actions slot's) visibility
   * based on right-panel state.
   * @param {boolean} hidden - True to hide, false to show
   * @private
   */
  _setCloseButtonHiddenForPanel(hidden) {
    if (this.closeButton) {
      this.closeButton.classList.toggle('page-toolbar-header__close-btn--panel-hidden', hidden);
    }
    if (this.toolbarRightActions) {
      this.toolbarRightActions.classList.toggle('page-toolbar-header__right-actions--panel-hidden', hidden);
    }
  }

  /**
   * Coordinate page-level detail selection with non-routed page-info panels.
   *
   * Detail cards are active only while their routed detail is represented in
   * the right panel. Page-info panels replace that detail without closing the
   * panel, so the current section gets a chance to clear its own highlight,
   * detail state, and URL detail param before the panel content swaps.
   *
   * @param {Object} payload - right-panel:open payload
   * @private
   */
  _handleRightPanelOpen(payload = {}) {
    if (!this._isPageInfoContentType(payload.contentType)) return;

    const section = this.currentPage ? this.pageSections[this.currentPage] : null;
    if (!section || typeof section.clearActiveDetailSelection !== 'function') return;

    try {
      section.clearActiveDetailSelection();
    } catch (err) {
      console.warn(`[PageManager] clearActiveDetailSelection() threw for page "${this.currentPage}":`, err);
    }
  }

  /**
   * @param {string|null|undefined} contentType - Right-panel content type tag
   * @returns {boolean} True when the payload represents a page-info panel
   * @private
   */
  _isPageInfoContentType(contentType) {
    return typeof contentType === 'string' && contentType.endsWith('-info');
  }

  /**
   * Handle menu button click - toggle sidebar
   */
  handleMenuButtonClick() {
    if (this.eventBus) {
      this.eventBus.emit('sidebar:toggle');
    }
  }

  /**
   * Apply the rolled-up sidebar badge indicator (highest priority across all
   * sections) as a single dot on the toolbar menu button, so a collapsed
   * sidebar's notifications are still visible before opening it.
   */
  updateMenuButtonBadge() {
    if (!this.menuButton) return;

    const existingDot = this.menuButton.querySelector('.notification-dot');
    if (existingDot) existingDot.remove();

    const indicator = window.badgeService?.getAggregateIndicator?.();
    if (!indicator) return;

    const dot = document.createElement('span');
    dot.className = `notification-dot notification-dot--${indicator.color}`;
    dot.setAttribute('aria-hidden', 'true');
    this.menuButton.appendChild(dot);
  }

  /* ===== STATE MACHINE METHODS ===== */

  /**
   * Set the main state class
   * @param {string} state - 'dashboard', 'chat', 'fullpage', or 'orientation-initial'
   * @private
   */
  _setMainState(state) {
    if (!this.main) return;

    // Remove all state classes
    this.main.classList.remove(
      'main--dashboard',
      'main--chat',
      'main--fullpage',
      'main--orientation-initial'
    );

    // Add the new state class
    if (state) {
      this.main.classList.add(`main--${state}`);
      this.currentMainState = state;
    }
  }

  /**
   * Get the target main state for a page
   * @param {string|null} pageId - Page ID or null for dashboard
   * @returns {string} State name: 'dashboard', 'chat', or 'fullpage'
   * @private
   */
  _getStateForPage(pageId) {
    if (pageId === null) return 'dashboard';
    if (pageId === 'chat') return 'chat';
    if (this.fullpagePages.includes(pageId)) return 'fullpage';
    return 'chat'; // Default fallback
  }

  /* ===== ORIENTATION STATE METHODS ===== */

  /**
   * Set orientation-initial state (no header, no dashboard)
   * Used at the start of orientation for a clean onboarding experience
   */
  setOrientationInitialState() {
    // Lock header hidden — survives any page-driven unhide that races with us.
    this._orientationInitialActive = true;

    // Ensure chat page is visible
    if (this.chatContainer) {
      this.chatContainer.hidden = false;
    }

    // Hide header
    this._hideHeader();

    // Set main state
    this._setMainState('orientation-initial');

    console.log('[PageManager] Set orientation-initial state');
  }

  /**
   * Transition from orientation to normal chat state
   * Shows header with slide animation
   */
  transitionOrientationToChat() {
    this._orientationInitialActive = false;
    this._setMainState('chat');
    this._showHeader();
    console.log('[PageManager] Transitioned orientation to chat state');
  }

  /**
   * Hide the header with animation
   * @private
   */
  _hideHeader() {
    if (this.headerContainer) {
      this.headerContainer.classList.add('header-container--hidden');
    }
  }

  /**
   * Show the header with animation
   * @private
   */
  _showHeader() {
    if (this.headerContainer) {
      this.headerContainer.classList.remove('header-container--hidden');
    }
  }

  /**
   * Check if currently in an orientation state
   * @returns {boolean}
   */
  isInOrientationState() {
    return this.currentMainState === 'orientation-initial';
  }

  /**
   * Check if device is mobile (for skipping input slide animation)
   * @returns {boolean}
   * @private
   */
  _isMobile() {
    return window.innerWidth <= 640;
  }

  /**
   * Calculate the input slide distance for dashboard ↔ chat transition
   * @returns {number} Pixels to slide (positive = down)
   * @private
   */
  _calculateInputSlideDistance() {
    if (!this.chatInputArea || !this.main) return 0;

    const inputRect = this.chatInputArea.getBoundingClientRect();
    const mainRect = this.main.getBoundingClientRect();

    // Distance from current position to bottom of main
    const targetBottom = mainRect.bottom - 20; // Account for padding
    const currentBottom = inputRect.bottom;

    return targetBottom - currentBottom;
  }

  /**
   * Handle close button click - navigate back to home
   */
  handleCloseButtonClick() {
    console.log('[PageManager] Close button clicked, navigating to home');
    this.eventBus.emit('navigate', { page: 'chat' });
  }

  /**
   * Handle a page-info `?` click - open the active section's "About this page"
   * panel. The section owns the panel content and the `right-panel:open` emit;
   * this only routes the click, so a section that has no page info simply
   * doesn't render the `?` and never exposes showPageInfo().
   */
  handlePageInfoClick() {
    const section = this.currentPage ? this.pageSections[this.currentPage] : null;
    if (typeof section?.showPageInfo !== 'function') return;

    try {
      section.showPageInfo();
    } catch (err) {
      console.warn(`[PageManager] showPageInfo() threw for page "${this.currentPage}":`, err);
    }
  }

  /**
   * Show or hide the toolbar based on current page
   * @param {string|null} pageId - Current page ID
   */
  updateToolbarVisibility(pageId) {
    if (!this.toolbar) return;

    const shouldShow = this.pagesWithToolbar.includes(pageId);

    if (shouldShow) {
      this.toolbar.classList.add('page-toolbar-header--visible');
    } else {
      this.toolbar.classList.remove('page-toolbar-header--visible');
      // Clear toolbar actions when hiding
      this.clearToolbarActions();
    }
  }

  /**
   * Set toolbar action buttons
   * @param {HTMLElement|DocumentFragment|null} content - Action buttons to display
   * @param {string} [section] - Section identifier for CSS visibility control
   */
  setToolbarActions(content, section = null) {
    if (!this.toolbarActions) return;

    // Clear existing actions
    this.toolbarActions.innerHTML = '';

    // Set section data attribute for CSS visibility control
    if (section) {
      this.toolbar?.setAttribute('data-actions-section', section);
    } else {
      this.toolbar?.removeAttribute('data-actions-section');
    }

    // Add new content if provided
    if (content) {
      this.toolbarActions.appendChild(content);
    }
  }

  /**
   * Clear toolbar action buttons — both the centered actions slot and the
   * right-side slot sections mount their desktop primary action into
   * (#page-toolbar-header-right-actions). Called on every page change, so a
   * section that forgets to unmount in deactivate() can't leak its button.
   */
  clearToolbarActions() {
    if (this.toolbarActions) {
      this.toolbarActions.innerHTML = '';
    }
    if (this.toolbarRightActions) {
      this.toolbarRightActions.innerHTML = '';
    }
    // Remove section data attribute
    this.toolbar?.removeAttribute('data-actions-section');
  }

  /**
   * Get the toolbar actions container for external use
   * @returns {HTMLElement|null}
   */
  getToolbarActionsContainer() {
    return this.toolbarActions;
  }

  /**
   * Update sidebar active state to reflect current page.
   *
   * Single funnel for both dashboard entry paths: the instant branch of
   * showDashboard() calls this directly, and the animated chat → dashboard
   * branch reaches it via _updatePageState(null).
   *
   * @param {string|null} pageId - Current page ID or null for dashboard
   */
  updateSidebarActiveState(pageId) {
    // Remove active class from all activatable sidebar items
    this.activatableSidebarItems.forEach(item => {
      item.classList.remove('sidebar__item--active');
    });

    // The dashboard has no sidebar page of its own — "New chat" stands in for it.
    if (pageId === null) {
      document.querySelector('.sidebar__item--new-chat')
        ?.classList.add('sidebar__item--active');
      return;
    }

    // Only set active for assessments, practices, insights, discovery, and
    // notes pages — not search; see the cache above for why.
    const activatablePages = ['assessments', 'practices', 'insights', 'discovery', 'notes'];
    if (activatablePages.includes(pageId)) {
      const activeItem = document.querySelector(`.sidebar__item[data-page="${pageId}"]`);
      if (activeItem) {
        activeItem.classList.add('sidebar__item--active');
      }
    }
  }

  /**
   * Show a specific page and hide all others.
   *
   * Concurrency model:
   *   - Every call enqueues onto `_navQueue` and is tagged with a fresh
   *     `_navToken`. A new call increments the token and supersedes any
   *     queued-but-not-yet-running predecessors.
   *   - When a task's turn arrives, it checks whether its token is still
   *     the latest; if not, it resolves `false` without running any DOM
   *     work. The resolved-false call is the route handler's signal to
   *     skip `section.activate()`.
   *   - In-flight transitions are NOT cancelled — the CSS animations run
   *     to completion. The next queued task starts immediately after.
   *
   * Returns:
   *   - `true`: route handler should call `section.activate(...)`. Either
   *     the transition completed and we're now on `pageId`, or we were
   *     already there (same-page re-entry; URL-param-driven sections need
   *     `activate()` to re-apply state).
   *   - `false`: bad pageId, or this call was superseded. Route handler
   *     must skip activation.
   *
   * @param {string} pageId - Page identifier (key in `this.pages`)
   * @returns {Promise<boolean>}
   */
  showPage(pageId) {
    if (!this.pages[pageId]) {
      console.error(`[PageManager] Page not found: ${pageId}`);
      return Promise.resolve(false);
    }

    const myToken = ++this._navToken;

    const myTurn = this._navQueue.then(async () => {
      // Superseded by a later call before our turn arrived.
      if (myToken !== this._navToken) return false;

      // A predecessor's transition may have already landed us on the
      // target (e.g. queue collapsed an A→B→A round-trip).
      if (this.currentPage === pageId) return true;

      this.previousPage = this.currentPage;
      const fromState = this._getStateForPage(this.previousPage);
      const toState = this._getStateForPage(pageId);

      // Initial load — no main state set yet.
      if (!this.currentMainState) {
        this.deactivateCurrent();
        this._instantSwitch(pageId);
        return true;
      }

      const isDashboardChat =
        (fromState === 'dashboard' && toState === 'chat') ||
        (fromState === 'chat' && toState === 'dashboard');

      if (isDashboardChat) {
        await this._animatedDashboardChatTransition(pageId, fromState, toState);
      } else {
        await this._animatedTransition(pageId, toState);
      }
      return true;
    });

    // Tail of the queue swallows rejections so a thrown transition
    // doesn't poison subsequent navigations.
    this._navQueue = myTurn.catch(() => {});
    return myTurn;
  }

  /**
   * Perform instant page switch (no animation)
   * @param {string} pageId - Target page ID
   * @private
   */
  _instantSwitch(pageId) {
    const targetState = this._getStateForPage(pageId);

    // Set main state (handles dashboard/page-container/input visibility)
    this._setMainState(targetState);

    // Hide all pages within page-container
    Object.entries(this.pages).forEach(([id, pageEl]) => {
      if (pageEl) {
        pageEl.hidden = true;
        // Remove any lingering transition classes
        pageEl.classList.remove('page--fading', 'chat-container--fading');
      }
    });

    // Show requested page (if not dashboard)
    if (pageId && this.pages[pageId]) {
      this.pages[pageId].hidden = false;
    }

    this.currentPage = pageId;

    // Update header visibility (instant)
    this._updateHeaderVisibility(pageId);

    // Update UI state
    this._updatePageState(pageId);
  }

  /**
   * Perform standard animated page transition (not dashboard ↔ chat)
   * @param {string} pageId - Target page ID
   * @param {string} toState - Target main state
   * @returns {Promise<boolean>} Resolves with `true` once page is visible; route handler should activate the section
   * @private
   */
  _animatedTransition(pageId, toState) {
    return new Promise((resolve) => {
      this.isTransitioning = true;

      const currentPageEl = this.previousPage ? this.pages[this.previousPage] : null;
      const targetPageEl = this.pages[pageId];

      // Phase 1: Fade out current page + slide header (simultaneously)
      if (currentPageEl) {
        const fadeClass = this.previousPage === 'chat' ? 'chat-container--fading' : 'page--fading';
        currentPageEl.classList.add(fadeClass);
      }

      // Update header visibility
      this._updateHeaderVisibility(pageId);

      // Phase 2: After fade-out completes, switch state and fade in
      setTimeout(() => {
        // Tear down outgoing section now — page is invisible, so any DOM
        // changes from destroy() (e.g. ResultsFilterBar emptying its mount)
        // happen off-screen and don't cause a visible layout shift.
        this.deactivateCurrent();

        // Set main state
        this._setMainState(toState);

        // Hide old page
        if (currentPageEl) {
          currentPageEl.hidden = true;
          currentPageEl.classList.remove('page--fading', 'chat-container--fading');
        }

        // Hide all other pages
        Object.entries(this.pages).forEach(([id, pageEl]) => {
          if (pageEl && id !== pageId) {
            pageEl.hidden = true;
          }
        });

        // Prepare target page for fade-in
        const fadeInClass = pageId === 'chat' ? 'chat-container--fading' : 'page--fading';
        targetPageEl.classList.add(fadeInClass);
        targetPageEl.hidden = false;

        // Trigger fade-in (next frame to ensure transition fires)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            targetPageEl.classList.remove(fadeInClass);
          });
        });

        this.currentPage = pageId;

        // Update UI state
        this._updatePageState(pageId);

        // Resolve with `true` — page is visible, route handler should activate.
        resolve(true);

        // Clear transitioning flag after fade-in completes
        setTimeout(() => {
          this.isTransitioning = false;
        }, this.fadeInDuration);

      }, this.fadeOutDuration);
    });
  }

  /**
   * Animate dashboard ↔ chat transition using FLIP technique for input
   *
   * Uses crossfade for content and FLIP (First-Last-Invert-Play) for input:
   * - Content fades out (200ms), state switches while invisible, content fades in
   * - Input position is recorded, state switches, inverse transform applied, then animated
   *
   * @param {string} pageId - Target page ID ('chat' or null for dashboard)
   * @param {string} fromState - Current state ('dashboard' or 'chat')
   * @param {string} toState - Target state ('dashboard' or 'chat')
   * @returns {Promise<boolean>} Resolves with `true` when transition is complete; route handler should activate the section
   * @private
   */
  _animatedDashboardChatTransition(pageId, fromState, toState) {
    return new Promise((resolve) => {
      this.isTransitioning = true;
      const isMobile = this._isMobile();
      const isGoingToChat = toState === 'chat';
      const dashboardContent = this.dashboard?.querySelector('.dashboard__content');

      // FLIP animation duration
      const flipDuration = 300;

      // Phase 1: Start content fade, record input position for FLIP
      this.main?.classList.add('main--transitioning');

      // Collapse a sticky-expanded input to one row BEFORE measuring its position,
      // so a tall draft can't shift `top` and cause a FLIP jump. Instant (no height
      // transition); page:changed fires after the switch, so it's too late there.
      window.app?.modules?.input?.resetInputHeight?.();

      let inputStartY = 0;
      if (!isMobile && this.chatInputArea) {
        inputStartY = this.chatInputArea.getBoundingClientRect().top;
      }

      if (isGoingToChat) {
        this.main?.classList.add('main--fade-out-dashboard');
      } else {
        this.headerContainer?.classList.add('header-container--hidden');
        this.chatContainer?.classList.add('chat-container--fading');
      }

      // Phase 2: Content invisible - switch state and FLIP animate input
      setTimeout(() => {
        if (isGoingToChat) {
          this.chatContainer.classList.add('chat-container--fading');
          this.chatContainer.hidden = false;
          this._setMainState(toState);

          // FLIP: Apply inverse transform then animate to final position
          if (!isMobile && this.chatInputArea) {
            const deltaY = inputStartY - this.chatInputArea.getBoundingClientRect().top;
            this.chatInputArea.style.transition = 'none';
            this.chatInputArea.style.transform = `translateY(${deltaY}px)`;
            this.chatInputArea.offsetHeight; // Force reflow
            this.chatInputArea.style.transition = `transform ${flipDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
            this.chatInputArea.style.transform = '';
          }

          if (!this._orientationInitialActive) {
            this.headerContainer?.classList.remove('header-container--hidden');
          }
          requestAnimationFrame(() => {
            this.chatContainer.classList.remove('chat-container--fading');
          });
        } else {
          if (dashboardContent) {
            dashboardContent.style.opacity = '0';
          }

          const inputStartYReverse = !isMobile && this.chatInputArea
            ? this.chatInputArea.getBoundingClientRect().top
            : 0;

          this._setMainState(toState);

          // FLIP for reverse direction
          if (!isMobile && this.chatInputArea) {
            const deltaY = inputStartYReverse - this.chatInputArea.getBoundingClientRect().top;
            this.chatInputArea.style.transition = 'none';
            this.chatInputArea.style.transform = `translateY(${deltaY}px)`;
            this.chatInputArea.offsetHeight;
            this.chatInputArea.style.transition = `transform ${flipDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
            this.chatInputArea.style.transform = '';
          }

          requestAnimationFrame(() => {
            if (dashboardContent) {
              dashboardContent.style.opacity = '';
            }
          });
        }

        this.currentPage = pageId;
        this._updatePageState(pageId);
        resolve(true);
      }, this.fadeOutDuration);

      // Phase 3: Clean up
      setTimeout(() => {
        if (!isMobile && this.chatInputArea) {
          this.chatInputArea.style.transition = '';
          this.chatInputArea.style.transform = '';
        }
        this.chatContainer?.classList.remove('chat-container--fading');
        this.main?.classList.remove('main--transitioning', 'main--fade-out-dashboard');
        this.isTransitioning = false;
      }, this.fadeOutDuration + flipDuration + 50);
    });
  }

  /**
   * Update header visibility based on page
   * Skips CSS transition on initial page load to prevent flash
   * @param {string} pageId - Current page ID
   * @private
   */
  _updateHeaderVisibility(pageId) {
    if (!this.headerContainer) return;

    const shouldHide = this.pagesWithHiddenHeader.includes(pageId);
    const headerElement = this.headerContainer.querySelector('.header');

    // Skip transition on initial page load to prevent flash
    if (!this.hasSetInitialPage) {
      // Temporarily disable CSS transitions on container AND header
      this.headerContainer.style.transition = 'none';
      if (headerElement) {
        headerElement.style.transition = 'none';
      }
      // Force reflow to ensure styles are applied
      this.headerContainer.offsetHeight;
    }

    if (shouldHide) {
      this.headerContainer.classList.add('header-container--hidden');
    } else if (!this._orientationInitialActive) {
      // Orientation owns the hidden state during the pre-name step; don't fight it.
      this.headerContainer.classList.remove('header-container--hidden');
    }

    // Re-enable transitions after initial state is set
    if (!this.hasSetInitialPage) {
      this.hasSetInitialPage = true;
      // Re-enable transitions on next frame
      requestAnimationFrame(() => {
        this.headerContainer.style.transition = '';
        if (headerElement) {
          headerElement.style.transition = '';
        }
      });
    }
  }

  /**
   * Update page-related UI state (sidebar, toolbar, etc.)
   * @param {string} pageId - Current page ID
   * @private
   */
  _updatePageState(pageId) {
    // Update sidebar active state
    this.updateSidebarActiveState(pageId);

    // Update toolbar visibility
    this.updateToolbarVisibility(pageId);

    // Update document title (null pageId = dashboard)
    const label = pageId ? this.pageTitles[pageId] : 'Dashboard';
    document.title = label ? `${label} | Encouple` : 'encouple';

    this.clearToolbarActions();

    // Emit event
    if (this.eventBus) {
      this.eventBus.emit('page:changed', {
        page: pageId,
        previousPage: this.previousPage
      });
    }
  }

  /**
   * Show the dashboard and hide all pages.
   *
   * Shares the showPage queue + supersession contract: rapid showPage ↔
   * showDashboard sequences serialize cleanly and only the latest target
   * runs to completion. Returns Promise<boolean> for symmetry with
   * showPage; existing fire-and-forget callers (router.js showDashboard
   * helper) still work unchanged.
   *
   * @returns {Promise<boolean>}
   */
  showDashboard() {
    const myToken = ++this._navToken;

    const myTurn = this._navQueue.then(async () => {
      if (myToken !== this._navToken) return false;

      if (this.currentPage === null && this.currentMainState === 'dashboard') {
        return true;
      }

      // Leaving orientation entirely — release the header lock so the dashboard
      // (and any subsequent page) can manage header visibility normally.
      this._orientationInitialActive = false;

      // Tear down the outgoing section before any state mutation.
      this.deactivateCurrent();

      this.previousPage = this.currentPage;
      const fromState = this._getStateForPage(this.previousPage);

      // Animate if coming from chat
      if (this.previousPage && fromState === 'chat') {
        await this._animatedDashboardChatTransition(null, 'chat', 'dashboard');
        return true;
      }

      // Instant switch for initial load or other cases
      this._setMainState('dashboard');

      // Hide all pages and remove transition classes
      Object.entries(this.pages).forEach(([id, pageEl]) => {
        if (pageEl) {
          pageEl.hidden = true;
          pageEl.classList.remove('page--fading', 'chat-container--fading');
        }
      });

      this.currentPage = null;

      // Hide header on dashboard (logo is shown in dashboard content).
      // Skip transition on initial page load to prevent flash.
      if (this.headerContainer) {
        const headerElement = this.headerContainer.querySelector('.header');

        if (!this.hasSetInitialPage) {
          this.headerContainer.style.transition = 'none';
          if (headerElement) {
            headerElement.style.transition = 'none';
          }
          this.headerContainer.offsetHeight; // Force reflow
        }

        this.headerContainer.classList.add('header-container--hidden');

        if (!this.hasSetInitialPage) {
          this.hasSetInitialPage = true;
          requestAnimationFrame(() => {
            this.headerContainer.style.transition = '';
            if (headerElement) {
              headerElement.style.transition = '';
            }
          });
        }
      }

      // Clear sidebar active state (no page is active on dashboard)
      this.updateSidebarActiveState(null);

      // Hide toolbar on dashboard
      this.updateToolbarVisibility(null);

      // Update document title for dashboard
      document.title = 'Dashboard | Encouple';

      // Emit event
      if (this.eventBus) {
        this.eventBus.emit('page:changed', {
          page: 'dashboard',
          previousPage: this.previousPage
        });
      }
      return true;
    });

    this._navQueue = myTurn.catch(() => {});
    return myTurn;
  }

  /**
   * Hide a specific page
   * @param {string} pageId - Page identifier
   */
  hidePage(pageId) {
    if (this.pages[pageId]) {
      this.pages[pageId].hidden = true;
      if (this.currentPage === pageId) {
        this.currentPage = null;
      }
    }
  }

  /**
   * Get the current active page
   * @returns {string|null} Current page ID or null if dashboard
   */
  getCurrentPage() {
    return this.currentPage;
  }

  /**
   * Check if a specific page is currently active
   * @param {string} pageId - Page identifier
   * @returns {boolean}
   */
  isPageActive(pageId) {
    return this.currentPage === pageId;
  }

  /**
   * Check if dashboard is currently visible
   * @returns {boolean}
   */
  isDashboardVisible() {
    return this.currentPage === null;
  }

  /**
   * Check if the dashboard is the settled current view.
   *
   * Stricter than isDashboardVisible(): a null currentPage also covers the
   * orientation-initial state, which is not the dashboard. Mirrors the
   * short-circuit guard in showDashboard().
   * @returns {boolean}
   */
  isDashboardActive() {
    return this.currentPage === null && this.currentMainState === 'dashboard';
  }
}
